type TranslateFn = (key: string, options?: any) => string;

export type UserFacingErrorLabels = {
    fallback: string;
    requiredFields: string;
    invalidValues: string;
};

const requiredFieldPatterns = [
    /cannot accept null for non-nullable input/i,
    /expected non-nullable type/i,
    /non-nullable type .* not to be null/i,
    /field .* was not provided/i,
    /variable .* was not provided/i
];

const invalidValuePatterns = [
    /got invalid value/i,
    /cannot represent non numeric value/i,
    /cannot represent non-integer value/i,
    /cannot represent a non string value/i,
    /cannot represent a non boolean value/i,
    /cannot represent following value/i,
    /one or more numeric values are invalid/i
];

const genericTechnicalPatterns = [
    /response not successful/i,
    /received status code 400/i,
    /unexpected execution error/i,
    /graphql/i
];

const trimMessage = (value: unknown): string =>
    typeof value === "string" ? value.trim() : "";

const pushUniqueMessage = (messages: string[], value: unknown) => {
    const message = trimMessage(value);
    if (!message || messages.includes(message)) {
        return;
    }

    messages.push(message);
};

export const buildUserFacingErrorLabels = (
    t: TranslateFn,
    fallback?: string
): UserFacingErrorLabels => ({
    fallback: fallback ?? t("error-occurred", {defaultValue: "An error occurred"}),
    requiredFields: t("validation.complete-required-fields", {
        defaultValue: "Check the required fields and try again."
    }),
    invalidValues: t("validation.correct-invalid-values", {
        defaultValue: "Check the entered values and try again."
    })
});

export const extractErrorMessages = (error: any): string[] => {
    const messages: string[] = [];
    const graphQlErrors = [
        ...(Array.isArray(error?.errors) ? error.errors : []),
        ...(Array.isArray(error?.graphQLErrors) ? error.graphQLErrors : []),
        ...(Array.isArray(error?.networkError?.result?.errors) ? error.networkError.result.errors : []),
        ...(Array.isArray(error?.networkError?.response?.errors) ? error.networkError.response.errors : [])
    ];

    graphQlErrors.forEach((graphQlError: any) => {
        pushUniqueMessage(messages, graphQlError?.message);
    });

    pushUniqueMessage(messages, error?.message);

    return messages;
};

export const localizeUserFacingErrorMessage = (
    value: unknown,
    t: TranslateFn,
    labels: UserFacingErrorLabels
): string => {
    const message = trimMessage(value);

    if (!message) {
        return labels.fallback;
    }

    if (requiredFieldPatterns.some(pattern => pattern.test(message))) {
        return labels.requiredFields;
    }

    if (invalidValuePatterns.some(pattern => pattern.test(message))) {
        return labels.invalidValues;
    }

    if (genericTechnicalPatterns.some(pattern => pattern.test(message))) {
        return labels.fallback;
    }

    return t(message, {defaultValue: message});
};

export const getUserFacingErrorMessage = (
    error: unknown,
    t: TranslateFn,
    labels: UserFacingErrorLabels
): string => {
    if (typeof error === "string") {
        return localizeUserFacingErrorMessage(error, t, labels);
    }

    const messages = extractErrorMessages(error);

    if (messages.length === 0) {
        return labels.fallback;
    }

    return localizeUserFacingErrorMessage(messages[0], t, labels);
};

export const containsNonFiniteNumbers = (value: unknown): boolean => {
    if (typeof value === "number") {
        return !Number.isFinite(value);
    }

    if (Array.isArray(value)) {
        return value.some(containsNonFiniteNumbers);
    }

    if (!value || typeof value !== "object") {
        return false;
    }

    return Object.values(value).some(containsNonFiniteNumbers);
};
